<script setup lang="ts">
import { computed } from 'vue'
import { useSeoHead } from '~/composables/useSeo/useSeoHead'

const layoutConfigStore = useLayoutConfigStore()
const localePath = useLocalePath()
const { locale } = useI18n()

const isMounted = ref(false)

onMounted(() => {
  nextTick(() => (isMounted.value = true))
})

onUnmounted(() => {
  isMounted.value = false
})

const SEO = computed(() => {
  const localePrefix = locale.value?.split('-')[0]?.toLowerCase()

  const seoTitleByLocale: Record<string, string> = {
    pt: 'Política de Privacidade',
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  }

  const seoDescriptionByLocale: Record<string, string> = {
    pt: 'Política de Privacidade',
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  }

  const breadcrumbTitle: Record<string, string> = {
    pt: 'Política de Privacidade',
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  }

  return {
    title: seoTitleByLocale[localePrefix ?? 'pt'] ?? seoTitleByLocale.pt,
    description: seoDescriptionByLocale[localePrefix ?? 'pt'] ?? seoDescriptionByLocale.pt,
    canonicalUrl: '',
    breadcrumbTitle: breadcrumbTitle[localePrefix ?? 'pt'] ?? breadcrumbTitle.pt,
    noIndex: false,
    noFollow: false
  } as import('~/types/seo').Seo
})

useSeoHead({
  seo: SEO,
  locale,
  addXDefaultAlternate: true
})

watchEffect(() => {
  if (isMounted.value) {
    layoutConfigStore.breadcrumbs = [{ label: 'Home', to: localePath('/') }, { label: 'Política de Privacidade' }]
    layoutConfigStore.layoutTheme = 'default'
    layoutConfigStore.secondaryLogo = ''
  }
})
</script>

<template>
  <section v-if="locale == 'pt-BR'" class="container">
    <h1>1 SOBRE A NOSSA POLÍTICA</h1>

    <p>
      A Aviva (Companhia Thermas do Rio Quente – CNPJ: 01.540.533/0001-29, Sauipe S/A: CNPJ 00.866.577/0001-80, Valetur
      – CNPJ: 02.082.394/0001-08), entendendo a importância do tema e em consonância à Lei Geral de Proteção de Dados
      Pessoais (Lei nº 13.709/18), reforça o compromisso com a segurança dos seus dados, priorizando a ética e a
      conformidade com as legislações.
    </p>

    <p>
      Pelo respeito a sua privacidade e prezando pela transparência, a Aviva criou esta Política de Privacidade e
      Cookies para demonstrar a você os dados pessoais que são coletados, utilizados, armazenados, compartilhados e os
      seus direitos como titular de dados pessoais.
    </p>

    <p>
      Esta política se aplica aos indivíduos (“Titular dos dados”, “Titular”, “Você”) que interagem com os produtos e
      serviços da Aviva, sejam clientes ou potenciais clientes, terceiros, visitantes do nosso site ou candidatos a
      vagas de emprego, e quem tem seus dados pessoais tratados pela Aviva.
    </p>

    <h2>2 FINALIDADES, DADOS COLETADOS E HIPÓTESES DE TRATAMENTO</h2>

    <p>Os dados são coletados com base nas seguintes hipóteses de tratamento, admitidas pela LGPD:</p>

    <table>
      <thead>
        <tr>
          <th>Finalidades</th>
          <th>Dados Coletados</th>
          <th>Hipóteses de Tratamento</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td data-label="Finalidades">Ofertas de produtos e serviços</td>
          <td data-label="Dados Coletados">
            Podemos coletar os seguintes dados:
            <ul class="table-list">
              <li>• Nome e iniciais – Nome;</li>
              <li>
                • Características Pessoais – Estado civil | Lazer e interesses |Preferência de compra | Perfil
                comportamental;
              </li>
              <li>
                • Informações de Contato – Endereço Pessoal | Telefone E-mail pessoal | Número de celular pessoal;
              </li>
              <li>• Informações Profissionais - Ocupação/Cargo.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">Legítimo interesse (art. 7º, inc. IX, LGPD).</td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Aquisição pelo Cliente, de serviços, produtos, hospedagem e pacotes de viagens, bem como reserva de hotel
          </td>
          <td data-label="Dados Coletados">
            Podemos coletar os seguintes dados:
            <ul class="table-list">
              <li>• Nome e iniciais - Nome Completo;</li>
              <li>• Características Pessoais - Data de nascimento | Sexo;</li>
              <li>• Identificação gerada por órgãos oficiais - CPF | RG | Passaporte;</li>
              <li>
                • Informações de Contato - Endereço Residencial | Telefone Residencial | E-mail pessoal | Número de
                celular pessoal;
              </li>
              <li>• Informações Financeiras – Dados de cartão de crédito/débito;</li>
              <li>• Informações de Crianças - Nome | Data de nascimento | Sexo | CPF | RG | Passaporte;</li>
              <li>• Informações de Adolescentes - Nome | Data de nascimento | Sexo |CPF | RG | Passaporte.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">
            Os dados são coletados para cumprimento de obrigação legal (Decreto nº 7.381, de 02 de dezembro de 2010 c/c
            art. 7º, inc. I, LGPD c/c Art. 26 da Lei 11.771/2008 e Art. 7º, IV, b do Regulamento Geral dos Meios de
            Hospedagem), bem como para solicitação de reembolso, nos casos aplicáveis, e, internamente, para conciliação
            de pagamento, emissão de notas fiscais e outras rotinas operacionais internas.<br /><br />Durante a vigência
            do contrato, os dados serão tratados com base na hipótese de execução de contrato (art 7º, inc. V, LGPD).
            Findada a relação entre as Partes, os dados serão mantidos pelo prazo prescricional, bem como legalmente
            exigido, de acordo com a hipótese de tratamento de exercício regular de direitos (art. 7º, inc. VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">Aquisição pelo Cliente de ingressos do Hot Park e Lazeres Opcionais.</td>
          <td data-label="Dados Coletados">
            Podemos coletar os seguintes dados:
            <ul class="table-list">
              <li>• Nome e iniciais - Nome Completo;</li>
              <li>• Características Pessoais - Data de nascimento | Sexo;</li>
              <li>• Identificação gerada por órgãos oficiais - CPF | RG;</li>
              <li>• Informações de Contato - Endereço Residencial | Telefone Residencial | E-mail pessoal.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">
            Os dados são utilizados para a Execução de contrato (art 7º, inc. V, LGPD), o qual, uma vez finalizado,
            permite a manutenção dos dados pelo período prescricional e legalmente exigido de acordo com a hipótese de
            tratamento de exercício regular de direitos (art. 7º, inc. VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">Navegações em nossos sites</td>
          <td data-label="Dados Coletados">
            Dados do dispositivo, informações sobre a utilização do site (hora de acesso, páginas acessadas), logs de
            acesso.
          </td>
          <td data-label="Hipóteses de Tratamento">
            Em se tratando de logs de acesso, eles são coletados para cumprimento de obrigação legal (art. 15 do Marco
            Civil da Internet); quanto aos demais, os dados são coletados de acordo com o Legítimo interesse (art. 7º,
            inc. IX, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Atendimento ao cliente<br />(esse contato poderá contemplar diversos assuntos, tais como: respostas a
            dúvidas, respostas de reclamações e solicitações, atualizações dos pedidos realizados, dentre outros,
            atendimento de solicitações através de nossa Central de Atendimento)
          </td>
          <td data-label="Dados Coletados">
            <ul class="table-list">
              <li>• Nome e iniciais - Nome Completo;</li>
              <li>
                • Características Pessoais - Idade | Data de nascimento | Local de Nascimento | Gênero | Nacionalidade |
                Naturalidade;
              </li>
              <li>• Identificação gerada por órgãos oficiais - CPF | RG | Passaporte;</li>
              <li>
                • Informações de Contato - Endereço Residencial | Telefone Residencial | E-mail pessoal | Número de
                celular pessoal;
              </li>
              <li>• Informações Profissionais - Ocupação/Cargo;</li>
              <li>• Informações de Crianças - Nome | | Data de nascimento | Endereço;</li>
              <li>• Informações de Adolescentes - Nome | | Data de nascimento | Endereço;</li>
              <li>• Informação dispositivo móveis - E-mail - conteúdo (dispositivos móveis).</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">
            Dependerá da finalidade do atendimento.<br />Caso seja um possível cliente interessado nos serviços
            oferecidos, o tratamento dar-se-á de acordo com a hipótese de tratamento de consentimento (art. 7º, inc. I,
            LGPD) ou procedimentos preliminares à execução de contrato (art. 7º, inc V., LGPD), caso haja um avanço,
            podendo, neste caso, ser utilizado para a execução de contrato (art 7º, inc. V, LGPD), hipótese de
            tratamento que também é utilizado para os atendimentos realizados para os casos em que o atendimento é
            direcionado aos titulares que já são clientes da Aviva.<br />Os dados, em todo o caso, serão mantidos pelo
            período prescricional, bem como pelo legalmente exigível, de acordo com a hipótese de tratamento de
            exercício regular de direitos (art. 7º, inc. VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Identificar você no acesso e durante a sua permanência nas instalações da Aviva
          </td>
          <td data-label="Dados Coletados">
            <ul class="table-list">
              <li>• Nome e iniciais - Nome Completo;</li>
              <li>• Identificação gerada por órgãos oficiais – CPF;</li>
              <li>• Informações de Contato - Número de celular pessoal;</li>
              <li>• Informações de Crianças – Nome.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">Legítimo interesse (art. 7º, inc. IX, LGPD).</td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Fornecer conteúdo personalizado e recomendações com base em suas atividades dos nossos serviços
          </td>
          <td data-label="Dados Coletados">
            <ul class="table-list">
              <li>• Nome e iniciais - Nome Completo;</li>
              <li>
                • Informações de Contato - Cidade | Telefone Residencial | E-mail pessoal | Número de celular pessoal;
              </li>
              <li>• Informações Profissionais - Ocupação/Cargo.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">Legítimo interesse (art. 7º, inc. IX, LGPD).</td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Prestação de informações obrigatórias em razão de lei – Preenchimento de FNRH
          </td>
          <td data-label="Dados Coletados">
            <ul class="table-list">
              <li>• Identificação gerada por órgãos oficiais - CPF | RG | Passaporte;</li>
              <li>
                • Informações de Contato - Endereço Residencial | Telefone Residencial | E-mail pessoal | Número de
                celular pessoal;
              </li>
              <li>• Motivo da viagem e Duração da Estadia;</li>
              <li>• Outras viagens realizadas para o destino e no ano;</li>
              <li>• Informações Profissionais - Ocupação/Cargo;</li>
              <li>• Informações de Crianças - Nome | | Data de nascimento | Endereço;</li>
              <li>• Informações de Adolescentes - Nome | | Data de nascimento | Endereço;</li>
              <li>• Informação dispositivo móveis - E-mail - conteúdo (dispositivos móveis).</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">
            Os dados são coletados para cumprimento de obrigação legal (Decreto nº 7.381, de 02 de dezembro de 2010 c/c
            art. 7º, inc. I, LGPD c/c Art. 26 da Lei 11.771/2008 e Art. 7º, IV, b do Regulamento Geral dos Meios de
            Hospedagem).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Contratação de colaboradores e/ou prestadores de serviços terceirizados, bem como realização do respectivo
            processo seletivo
          </td>
          <td data-label="Dados Coletados">
            <ul class="table-list">
              <li>• Nome e iniciais - Nome Completo;</li>
              <li>
                • Características Pessoais - Idade | Data de nascimento | Local de Nascimento | Gênero | | Nacionalidade
                | Naturalidade | Estado Civil | | Número de filhos | Preferências de navegação na Internet | Perfil
                comportamental;
              </li>
              <li>• Filiação - Nome da Mãe | Nome do Pai;</li>
              <li>• Identificação gerada por órgãos oficiais - CPF | RG | CNH | CTPS | Passaporte;</li>
              <li>• Informações de Contato - Endereço Residencial | E-mail pessoal | Número de celular pessoal;</li>
              <li>
                • Escolaridade - Diplomas e escolaridade | Licenças e associação profissional | Histórico acadêmico |
                Certificados;
              </li>
              <li>
                • Informações Profissionais - Ocupação/Cargo | Documentos de órgãos de classe | Endereço comercial |
                Telefone comercial | E-mail comercial | Celular comercial | Número de Identificação: Matrícula;
              </li>
              <li>• Informações Financeiras - Dados bancários (banco agência e conta);</li>
              <li>• Informações de Crianças - Nome | Idade | Sexo;</li>
              <li>• Informações de Adolescentes - Nome | Idade | Sexo;</li>
              <li>
                • Dados sensíveis gerais - Crenças religiosas ou filosóficas | Orientação sexual | Raça ou Origem Étnica
                | Dados de Saúde Ocupacional | Fotografia | Arquivo de vídeo.
              </li>
              <li>• Histórico Profissional</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">
            Em se tratando de processo seletivo, os dados serão tratados com base na hipótese de tratamento de
            procedimentos preliminares à execução de contrato (art. 7º, inc V., LGPD); avançando o processo seletivo, os
            dados serão utilizados para a Execução de contrato (art 7º, inc. V, LGPD). Neste caso, alguns dados também
            serão necessários por força de obrigação legal ou regulatória (art. 7º, inc. II, LGPD c/c art. 11, inc. II,
            alínea "a", LGPD).<br /><br />Ao fim do contrato com o colaborador ou prestador de serviço, os dados serão
            mantidos pelo prazo legalmente previsto ou pelo prazo prescricional, de acordo com a hipótese de tratamento
            de exercício regular de direitos (art. 7º, inc. VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">Medidas de prevenção e combate a fraudes e ilícitos</td>
          <td data-label="Dados Coletados">
            <ul class="table-list">
              <li>• Nome e iniciais - Nome Completo;</li>
              <li>• Características Pessoais - Data de nascimento | Sexo;</li>
              <li>• Identificação gerada por órgãos oficiais – CPF;</li>
              <li>
                • Informações de Contato - Endereço Residencial | Telefone Residencial | E-mail pessoal | Número de
                celular pessoal;
              </li>
              <li>• Informações de Crianças - Nome | Data de Nascimento | Sexo;</li>
              <li>• Informações de Adolescentes - Nome | Data de nascimento| Sexo.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">
            Proteção de crédito (art. 7º, inc. X, LGPD) e interesses legítimos (art. 7º, inc. IX, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">Eventos, divulgação de ações promocionais, lives</td>
          <td data-label="Dados Coletados">
            <ul class="table-list">
              <li>• Informações de Contato (nome, telefone, redes sociais, correio eletrônico).</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">
            A hipótese de tratamento dependerá do estágio da jornada do cliente.<br />No caso inicial da coleta, a
            hipótese será o consentimento (art. 7º, inc. I, LGPD).<br /><br />Caso haja o avanço em direção à
            contratação, a hipótese de tratamento será a de Procedimentos preliminares à execução de contrato (art. 7º,
            inc V., LGPD).<br /><br />Avançando, os dados serão utilizados para a Execução de contrato (art 7º, inc. V,
            LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Conteúdos gerados por você e com divulgação autorizada: conteúdos criados ou compartilhados publicamente por
            você, quando previamente autorizados.
          </td>
          <td data-label="Dados Coletados">
            <ul class="table-list">
              <li>• Recomendações, depoimentos, avaliações, comentários, imagens, fotos, dentre outros.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de Tratamento">Consentimento (art. 7º, inc. I, LGPD).</td>
        </tr>
      </tbody>
    </table>

    <h2>3 COM QUEM NÓS PODEMOS COMPARTILHAR OS DADOS PESSOAIS</h2>

    <p>
      A Aviva poderá compartilhar os seus dados caso você solicite, ou, ainda, quando necessário para uma finalidade
      legítima, para oferta de produtos e serviços, compartilhamento com prestadores de modo a cumprir o propósito
      negocial da AVIVA, todos de acordo com a tabela de finalidade contida nas hipóteses de tratamento de dados
      disposta mais acima. Desse modo, o compartilhamento dos seus dados também poderá ser feito com os nossos
      prestadores de serviços e parceiros, ou com autoridades governamentais, conforme abaixo:
    </p>

    <ul>
      <li>
        <b>Prestadores de serviços, fornecedores e parceiros:</b> para que possamos te proporcionar serviços e
        experiências de qualidade, contamos com a colaboração de diversos prestadores de serviços e parceiros que
        poderão tratar os dados pessoais coletados em nosso nome e de acordo com as nossas instruções, dentre os
        serviços prestados, poderemos ter: instituições financeiras, serviços de adquirência, análise de antifraude,
        prestadores de serviço de tecnologia diversos para análise de comportamento de consumo, geração de leads, oferta
        de produtos, serviços e cumprimento do proposito negocial da AVIVA, recrutamento e seleção de candidatos,
        campanhas de marketing, eventos e atrações e armazenamento em nuvem;
      </li>
      <li>
        <b>Autoridades governamentais:</b> para o cumprimento de obrigações legais ou regulatórias, os seus dados
        pessoais poderão ser compartilhados com as autoridades competentes;
      </li>
      <li>Em atendimento às ordens judiciais.</li>
    </ul>

    <h2>4 TRANSFERÊNCIA INTERNACIONAL DE DADOS PESSOAIS</h2>

    <p>
      Alguns de seus dados pessoais poderão ser transferidos para outros países, quando necessário para a execução de
      serviços relacionados às atividades da Aviva, tais como a utilização de serviços computacionais em nuvem, soluções
      tecnológicas, ou suporte operacional. Ou ainda com a colaboração de parceiros que armazenam seus dados
      internacionalmente.
    </p>

    <p>
      A Aviva possui credenciamento junto à uma intercambiadora, ficando admitida a extensão da utilização do direito de
      uso de ocupação por Sistema de Tempo Compartilhado ao sistema de intercâmbio de hospedagem. Assim, caso haja
      adesão ao programa de intercâmbio de semanas de hospedagem, será necessário o preenchimento do contrato da
      intercambiadora e os seus dados poderão ser compartilhados com ele para realizar a associação e através de
      contrato próprio o qual o próprio consumidor adere/contrata de forma adicional. Após a associação, o pagamento de
      taxas, agendamentos, reservas, dentre outros, serão negociados entre o cliente e a intercambiadora.
    </p>

    <p>
      Sempre que houver a necessidade de transferência internacional de dados pessoais, a Aviva adotará os mecanismos e
      salvaguardas previstos na Lei Geral de Proteção de Dados Pessoais (LGPD), especialmente aqueles estabelecidos no
      art. 33, bem como observará as diretrizes e regulamentos expedidos pela Agência Nacional de Proteção de Dados
      (ANPD).
    </p>

    <p>
      Nos termos da Resolução CD/ANPD nº 32, de 26 de janeiro de 2026, a União Europeia foi reconhecida como organismo
      internacional que proporciona grau de proteção de dados pessoais adequado ao previsto na LGPD. Assim, as
      transferências de dados pessoais para os Estados‑Membros da União Europeia, para os países integrantes do Espaço
      Econômico Europeu (Islândia, Liechtenstein e Noruega), bem como para as instituições, órgãos e agências da União
      Europeia, poderão ocorrer com fundamento no art. 33, inciso I, da LGPD, sem prejuízo do cumprimento das demais
      obrigações legais aplicáveis ao tratamento de dados pessoais.
    </p>

    <h2>5 RETENÇÃO E EXCLUSÃO DOS SEUS DADOS PESSOAIS</h2>

    <p>
      A Aviva tratará os seus dados pessoais durante todo o período em que você for nosso cliente, nosso colaborador,
      utilizar os nossos serviços e navegar em nossas plataformas ou quando for nosso potencial cliente com quem
      compartilhamos nossas ofertas e promoções. Os dados serão armazenados em ambiente seguro e controlado para
      garantir a sua privacidade.
    </p>

    <p>
      Para cumprimento de obrigações legais ou regulatórias, para o exercício regular de direitos em processos judicial,
      administrativo ou arbitral, ou, quando aplicável, para fins de auditorias, poderemos armazenar os seus dados
      pessoais por um período adicional, de acordo com a legislação aplicável.
    </p>

    <p>
      Os prazos de retenção dos dados pessoais observam a finalidade do tratamento, as obrigações legais e regulatórias
      aplicáveis, bem como as políticas internas da Aviva relativas à retenção e descarte de dados, nos termos do artigo
      16 da LGPD
    </p>

    <h2>6 SEUS DIREITOS COMO TITULAR DE DADOS PESSOAIS</h2>

    <p>
      A transparência sobre o tratamento dos seus dados pessoais é prioridade para a Aviva. Além das informações
      disponibilizadas nesta política, você também poderá exercer os direitos previstos no Art. 18 da LGPD, dentre eles:
    </p>

    <ul>
      <li>Confirmação da existência de tratamento;</li>
      <li>Acesso aos dados;</li>
      <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
      <li>
        Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a
        LGPD;
      </li>
      <li>
        Portabilidade dos dados a outro fornecedor de serviço ou produto, observados os segredos comercial e industrial;
      </li>
      <li>
        Eliminação dos dados pessoais tratados com o consentimento do titular, exceto nas hipóteses previstas na LGPD;
      </li>
      <li>Informação das entidades públicas e privadas com as quais a Aviva realizou uso compartilhado de dados;</li>
      <li>Informação sobre a possibilidade de não fornecer consentimento e sobre as consequências da negativa;</li>
      <li>Revogação do consentimento.</li>
    </ul>

    <p>
      É importante ressaltar que existem situações regradas por outras leis que poderão vir a restringir ou limitar
      alguns desses direitos, tais como: situações que envolvam segredos comerciais e industriais.
    </p>

    <p>
      Todas as solicitações serão tratadas mediante requisição e de forma gratuita, sendo submetidas a uma prévia
      avaliação da sua identidade. Nossos contatos estão disponíveis no item: “<a href="#fale-conosco"
        >10. FALE CONOSCO</a
      >”.
    </p>

    <h2>7 MEDIDAS DE SEGURANÇA</h2>

    <p>
      A Aviva tem um compromisso com a segurança e utiliza diversos tipos de medidas técnicas e administrativas para
      garantir a integridade dos seus dados pessoais.
    </p>

    <p>
      Informamos que nossos prestadores de serviços, fornecedores e parceiros passam por processo de
      <i>due diligence</i>, homologação e análise jurídica.
    </p>

    <p>
      Para as execuções das atividades internas, buscamos nos orientar por padrões e melhores práticas adotados pelo
      mercado.
    </p>

    <h2>8 USO DE COOKIES</h2>

    <h3>8.1 O que é?</h3>
    <p>
      Um cookie é um pequeno arquivo de computador que pode ou não ser enviado por um site de internet para o navegador
      do usuário, quando o usuário visita o site. Os cookies podem ser utilizados para melhorar a navegação e lembrar de
      informações sobre a atividade do visitante do site, permitindo uma navegação mais rápida e eficiente para o
      usuário.
    </p>

    <h3>8.2 Como o nosso site utiliza?</h3>
    <ul>
      <li>
        <b>Cookies essenciais:</b> são aqueles essenciais para o funcionamento dos nossos sites, assim como para a
        utilização de determinados recursos;
      </li>
      <li>
        <b>Cookies de desempenho:</b> objetivam aprimorar a sua experiência de navegação em nossos sites, tornando-a
        mais fluída e agradável;
      </li>
      <li>
        <b>Cookies analíticos:</b> podem coletar informações sobre como você navega e interage com nossos sites, quais
        páginas e quando foram acessadas;
      </li>
      <li>
        <b>Cookies de marketing:</b> são utilizados para oferecer descontos e promoções que possam ser do seu interesse.
        Caso queira se cadastrar em nossas newsletters, poderemos te encaminhar outras ofertas exclusivas;
      </li>
      <li>
        <b>Cookies de publicidade:</b> servem para direcionar a publicidade e objetivam exibir anúncios que possam ser
        do seu interesse;
      </li>
      <li>
        <b>Cookies de terceiros:</b> servem para analisar a utilização do nosso site, possibilitar o compartilhamento em
        redes sociais e fornecer publicidade.
      </li>
    </ul>

    <h3>8.3 Como desativar?</h3>
    <p>
      Você pode, a qualquer momento, modificar o seu navegador de internet para desativar os cookies. Para verificar o
      procedimento de desativação deles, consulte a documentação do seu respectivo navegador. Lembramos que ao
      desativá-los, a sua navegação em nosso site poderá se tornar limitada ou com algumas funcionalidades
      comprometidas.
    </p>

    <h2>9 ALTERAÇÕES DESTA POLÍTICA</h2>

    <p>
      A Aviva reserva-se o direito de alterar esta Política de Privacidade e Cookies a qualquer tempo, para refletir
      quaisquer alterações relevantes na legislação ou em nossas práticas.
    </p>

    <p>
      Sugerimos que você revisite periodicamente esta política sempre que tiver dúvidas e caso tenha algum
      questionamento, sinta-se à vontade para nos contatar. Nossas informações estão disponíveis no item: "<a
        href="#fale-conosco"
        >10. FALE CONOSCO</a
      >".
    </p>

    <h2 id="fale-conosco">10 FALE CONOSCO</h2>

    <p>
      Sempre que você tiver alguma dúvida sobre esta Política de Privacidade e Cookies, mesmo após sua leitura, ou por
      qualquer razão precisar se comunicar conosco para assuntos envolvendo os seus dados pessoais, a Aviva
      disponibiliza os canais abaixo:
    </p>

    <ul>
      <li>
        <b>Encarregado pelo Tratamento de Dados Pessoais:</b> Data Guide Consultoria e Soluções em Proteção de Dados
        LTDA. (CNPJ: 39.317.591/0001-60), por intermédio de Taynara Rodrigues Bernardo, representante legal da Data
        Guide;
      </li>
      <li>
        <b>Canal de Atendimento:</b>
        <a href="/portaldeprivacidade">https://www.aviva.com.br/portaldeprivacidade</a>;
      </li>
      <li>
        <b>Correspondência:</b> Rua Particular Complexo Turístico, Rio Quente Resorts, S/N, Esplanada, Rio Quente/GO –
        CEP 75.667-000.
      </li>
    </ul>

    <p>Estamos à disposição para esclarecer suas dúvidas e colocar você no controle dos seus dados pessoais.</p>
  </section>

  <section v-else-if="locale == 'en-US'" class="container">
    <h1>1 ABOUT OUR POLICY</h1>

    <p>
      Aviva (Companhia Thermas do Rio Quente – CNPJ: 01.540.533/0001-29; Sauipe S/A – CNPJ: 00.866.577/0001-80; Valetur
      – CNPJ: 02.082.394/0001-08), recognizing the importance of this matter and in accordance with the Brazilian
      General Data Protection Law (Law No. 13.709/18), reinforces its commitment to the security of your data,
      prioritizing ethics and compliance with applicable laws.
    </p>

    <p>
      Out of respect for your privacy and in the interest of transparency, Aviva has created this Privacy and Cookies
      Policy to demonstrate to you the personal data that are collected, used, stored, and shared, as well as your
      rights as a data subject.
    </p>

    <p>
      This Policy applies to individuals (“Data Subject,” “Subject,” “you”) who interact with Aviva’s products and
      services, whether customers or prospective customers, third parties, visitors to our website, or job applicants,
      and anyone whose personal data are processed by Aviva.
    </p>

    <h2>2 PURPOSES, DATA COLLECTED, AND LEGAL BASES FOR PROCESSING</h2>

    <p>Data are collected based on the following legal bases for processing permitted under the LGPD:</p>

    <table>
      <thead>
        <tr>
          <th>Purposes</th>
          <th>Data Collected</th>
          <th>Legal Bases for Processing</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td data-label="Purposes">Offers of products and services</td>
          <td data-label="Data Collected">
            We may collect the following data:
            <ul class="table-list">
              <li>• Name and initials – Name;</li>
              <li>
                • Personal characteristics – Marital status | Leisure and interests | Purchase preference | Behavioral
                profile;
              </li>
              <li>• Contact information – Home address | Phone | Personal email | Personal mobile number;</li>
              <li>• Professional information – Occupation/Position.</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">Legitimate interest (Art. 7, item IX, LGPD).</td>
        </tr>
        <tr>
          <td data-label="Purposes">
            Customer purchase of services, products, lodging, and travel packages, as well as hotel reservations
          </td>
          <td data-label="Data Collected">
            We may collect the following data:
            <ul class="table-list">
              <li>• Name and initials – Full name;</li>
              <li>• Personal characteristics – Date of birth | Sex;</li>
              <li>• Identification issued by official bodies – CPF | RG | Passport;</li>
              <li>• Contact information – Home address | Home phone | Personal email | Personal mobile number;</li>
              <li>• Financial information – Credit/debit card data;</li>
              <li>• Children’s information – Name | Date of birth | Sex | CPF | RG | Passport;</li>
              <li>• Adolescents’ information – Name | Date of birth | Sex | CPF | RG | Passport.</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">
            Data are collected to comply with a legal obligation (Decree No. 7,381, of December 2, 2010, in conjunction
            with Art. 7, item I, LGPD, in conjunction with Art. 26 of Law 11,771/2008 and Art. 7, IV, b of the General
            Regulation on Lodging Facilities), as well as for refund requests, where applicable, and, internally, for
            payment reconciliation, issuance of invoices, and other internal operational routines.<br /><br />During the
            term of the contract, the data will be processed based on contract performance (Art. 7, item V, LGPD). Once
            the relationship between the Parties ends, the data will be retained for the statute of limitations period
            and as otherwise legally required, based on the legal basis of the regular exercise of rights (Art. 7, item
            VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Purposes">Customer purchase of Hot Park tickets and Optional Leisure Activities.</td>
          <td data-label="Data Collected">
            We may collect the following data:
            <ul class="table-list">
              <li>• Name and initials – Full name;</li>
              <li>• Personal characteristics – Date of birth | Sex;</li>
              <li>• Identification issued by official bodies – CPF | RG;</li>
              <li>• Contact information – Home address | Home phone | Personal email.</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">
            Data are used for contract performance (Art. 7, item V, LGPD) and, once the contract has ended, allow
            retention for the statute of limitations period and as otherwise legally required under the legal basis of
            the regular exercise of rights (Art. 7, item VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Purposes">Browsing our websites</td>
          <td data-label="Data Collected">
            Device data, information about website use (access time, pages visited), access logs.
          </td>
          <td data-label="Legal Bases for Processing">
            With respect to access logs, they are collected to comply with a legal obligation (Art. 15 of the Marco
            Civil da Internet (Brazilian Civil Framework of the Internet)); as for the other data, they are collected
            based on legitimate interest (Art. 7, item IX, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Purposes">
            Customer service<br />(this contact may cover several matters, such as: answers to questions, responses to
            complaints and requests, updates on orders placed, among others, handling requests through our Customer
            Service Center)
          </td>
          <td data-label="Data Collected">
            <ul class="table-list">
              <li>• Name and initials – Full name;</li>
              <li>
                • Personal characteristics – Age | Date of birth | Place of birth | Gender | Nationality | Place of
                origin;
              </li>
              <li>• Identification issued by official bodies – CPF | RG | Passport;</li>
              <li>• Contact information – Home address | Home phone | Personal email | Personal mobile number;</li>
              <li>• Professional information – Occupation/Position;</li>
              <li>• Children’s information – Name | Date of birth | Address;</li>
              <li>• Adolescents’ information – Name | Date of birth | Address;</li>
              <li>• Mobile device information – Email – content (mobile devices).</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">
            It will depend on the purpose of the service interaction.<br />If you are a prospective customer interested
            in the services offered, processing will be based on consent (Art. 7, item I, LGPD) or pre-contractual
            procedures (Art. 7, item V, LGPD). If it progresses, it may be used for contract performance (Art. 7, item
            V, LGPD), which is also the legal basis used for service interactions with data subjects who are already
            Aviva customers.<br />In any case, the data will be retained for the statute of limitations period and as
            otherwise legally required, based on the legal basis of the regular exercise of rights (Art. 7, item VI,
            LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Purposes">Identify you upon entry and during your stay at Aviva’s facilities</td>
          <td data-label="Data Collected">
            <ul class="table-list">
              <li>• Name and initials – Full name;</li>
              <li>• Identification issued by official bodies – CPF;</li>
              <li>• Contact information – Personal mobile number;</li>
              <li>• Children’s information – Name.</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">Legitimate interest (Art. 7, item IX, LGPD).</td>
        </tr>
        <tr>
          <td data-label="Purposes">
            Provide personalized content and recommendations based on your activities in our services
          </td>
          <td data-label="Data Collected">
            <ul class="table-list">
              <li>• Name and initials – Full name;</li>
              <li>• Contact information – City | Home phone | Personal email | Personal mobile number;</li>
              <li>• Professional information – Occupation/Position.</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">Legitimate interest (Art. 7, item IX, LGPD).</td>
        </tr>
        <tr>
          <td data-label="Purposes">Provision of mandatory information as required by law – Completion of FNRH</td>
          <td data-label="Data Collected">
            <ul class="table-list">
              <li>• Identification issued by official bodies – CPF | RG | Passport;</li>
              <li>• Contact information – Home address | Home phone | Personal email | Personal mobile number;</li>
              <li>• Reason for travel and length of stay;</li>
              <li>• Other trips taken to the destination and during the year;</li>
              <li>• Professional information – Occupation/Position;</li>
              <li>• Children’s information – Name | Date of birth | Address;</li>
              <li>• Adolescents’ information – Name | Date of birth | Address;</li>
              <li>• Mobile device information – Email – content (mobile devices).</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">
            Data are collected to comply with a legal obligation (Decree No. 7,381, of December 2, 2010, in conjunction
            with Art. 7, item I, LGPD, in conjunction with Art. 26 of Law 11,771/2008 and Art. 7, IV, b of the General
            Regulation on Lodging Facilities).
          </td>
        </tr>
        <tr>
          <td data-label="Purposes">
            Hiring employees and/or outsourced service providers, as well as conducting the related recruitment process
          </td>
          <td data-label="Data Collected">
            <ul class="table-list">
              <li>• Name and initials – Full name;</li>
              <li>
                • Personal characteristics – Age | Date of birth | Place of birth | Sex | Nationality | Place of origin
                | Marital status | Number of children | Internet browsing preferences | Behavioral profile;
              </li>
              <li>• Parentage – Mother’s name | Father’s name;</li>
              <li>• Identification issued by official bodies – CPF | RG | CNH | CTPS | Passport;</li>
              <li>• Contact information – Home address | Personal email | Personal mobile number;</li>
              <li>
                • Education – Diplomas and education | Licenses and professional association | Academic history |
                Certificates;
              </li>
              <li>
                • Professional information – Occupation/Position | Professional council documents | Business address |
                Business phone | Business email | Business mobile | Identification number: Employee ID;
              </li>
              <li>• Financial information – Bank details (bank, branch, and account);</li>
              <li>• Children’s information – Name | Age | Sex;</li>
              <li>• Adolescents’ information – Name | Age | Sex;</li>
              <li>
                • General sensitive data – Religious or philosophical beliefs | Sexual orientation | Race or ethnic
                origin | Occupational health data | Photograph | Video file.
              </li>
              <li>• Work history</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">
            In recruitment processes, data will be processed based on pre-contractual procedures (Art. 7, item V, LGPD).
            If the recruitment process advances, data will be used for contract performance (Art. 7, item V, LGPD). In
            this case, some data will also be required to comply with a legal or regulatory obligation (Art. 7, item II,
            LGPD, in conjunction with Art. 11, item II, “a,” LGPD).<br /><br />At the end of the contract with the
            employee or service provider, data will be retained for the legally prescribed period or the statute of
            limitations period, based on the legal basis of the regular exercise of rights (Art. 7, item VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Purposes">Measures to prevent and combat fraud and unlawful acts</td>
          <td data-label="Data Collected">
            <ul class="table-list">
              <li>• Name and initials – Full name;</li>
              <li>• Personal characteristics – Date of birth | Sex;</li>
              <li>• Identification issued by official bodies – CPF;</li>
              <li>• Contact information – Home address | Home phone | Personal email | Personal mobile number;</li>
              <li>• Children’s information - Name | Date of birth | Sex;</li>
              <li>• Adolescents’ information - Name | Date of birth | Sex.</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">
            Credit protection (Art. 7, item X, LGPD) and legitimate interests (Art. 7, item IX, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Purposes">Events, promotion of promotional actions, live streams</td>
          <td data-label="Data Collected">
            <ul class="table-list">
              <li>• Contact information (name, phone, social media, email address).</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">
            The legal basis will depend on the stage of the customer journey.<br />In the initial collection, the legal
            basis will be consent (Art. 7, item I, LGPD).<br /><br />If it progresses toward contracting, the legal
            basis will be pre-contractual procedures (Art. 7, item V, LGPD).<br /><br />As it advances, data will be
            used for contract performance (Art. 7, item V, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Purposes">
            Content generated by you and disclosed with authorization: content created or publicly shared by you, when
            previously authorized.
          </td>
          <td data-label="Data Collected">
            <ul class="table-list">
              <li>• Recommendations, testimonials, reviews, comments, images, photos, among others.</li>
            </ul>
          </td>
          <td data-label="Legal Bases for Processing">Consent (Art. 7, item I, LGPD).</td>
        </tr>
      </tbody>
    </table>

    <h2>3 WITH WHOM WE MAY SHARE PERSONAL DATA</h2>

    <p>
      Aviva may share your data if you request it or, when necessary for a legitimate purpose, to offer products and
      services, and to share with service providers in order to fulfill AVIVA’s business purpose, all in accordance with
      the purposes table included under the legal bases for processing set out above. Accordingly, your data may also be
      shared with our service providers and partners, or with governmental authorities, as described below:
    </p>

    <ul>
      <li>
        <b>Service providers, suppliers, and partners:</b> so that we can provide you with quality services and
        experiences, we work with various service providers and partners who may process the personal data collected on
        our behalf and according to our instructions. These services may include financial institutions, payment
        processing (acquiring) services, anti-fraud analysis, technology service providers for consumer behavior
        analysis, lead generation, offering products and services and fulfilling AVIVA’s business purpose, recruitment
        and selection of candidates, marketing campaigns, events and attractions, cloud storage, and research
        institutions;
      </li>
      <li>
        <b>Governmental authorities:</b> to comply with legal or regulatory obligations, your personal data may be
        shared with the competent authorities;
      </li>
      <li>In compliance with court orders.</li>
    </ul>

    <h2>4 INTERNATIONAL TRANSFER OF PERSONAL DATA</h2>

    <p>
      Some of your personal data may be transferred to other countries when necessary for the performance of services
      related to Aviva’s activities, such as the use of cloud computing services, technological solutions, or
      operational support, or through collaboration with partners that store your data internationally.
    </p>

    <p>
      Aviva is accredited with an exchange company, and the extension of the use right for occupancy under a Time-Share
      System to a lodging exchange system is allowed. Therefore, if you enroll in the lodging-week exchange program, you
      will need to complete the exchange company’s agreement, and your data may be shared with it to process membership,
      through a separate agreement that the consumer enrolls in/contracts additionally. After membership, payment of
      fees, scheduling, reservations, among others, will be negotiated between the customer and the exchange company.
    </p>

    <p>
      Whenever an international transfer of personal data is required, Aviva will adopt the mechanisms and safeguards
      provided for in the Brazilian General Data Protection Law (LGPD), especially those set out in Art. 33, and will
      also observe the guidelines and regulations issued by the Brazilian National Data Protection Authority (ANPD).
    </p>

    <p>
      Under ANPD Resolution CD/ANPD No. 32, of January 26, 2026, the European Union was recognized as an international
      body that provides a level of personal data protection adequate to that provided for under the LGPD. Accordingly,
      transfers of personal data to the Member States of the European Union, to the countries that comprise the European
      Economic Area (Iceland, Liechtenstein, and Norway), as well as to the institutions, bodies, and agencies of the
      European Union, may occur pursuant to Art. 33, item I, of the LGPD, without prejudice to compliance with the other
      legal obligations applicable to the processing of personal data.
    </p>

    <h2>5 RETENTION AND DELETION OF YOUR PERSONAL DATA</h2>

    <p>
      Aviva will process your personal data throughout the period in which you are our customer, our employee, use our
      services, and browse our platforms, or when you are a prospective customer with whom we share our offers and
      promotions. The data will be stored in a secure and controlled environment to safeguard your privacy.
    </p>

    <p>
      To comply with legal or regulatory obligations, for the regular exercise of rights in judicial, administrative, or
      arbitration proceedings, or, when applicable, for audit purposes, we may store your personal data for an
      additional period, in accordance with applicable law.
    </p>

    <p>
      Personal data retention periods observe the purpose of processing, applicable legal and regulatory obligations,
      and Aviva’s internal policies on data retention and disposal, under Art. 16 of the LGPD.
    </p>

    <h2>6 YOUR RIGHTS AS A DATA SUBJECT</h2>

    <p>
      Transparency regarding the processing of your personal data is a priority for Aviva. In addition to the
      information provided in this Policy, you may also exercise the rights set out in Art. 18 of the LGPD, including:
    </p>

    <ul>
      <li>Confirmation of the existence of processing;</li>
      <li>Access to the data;</li>
      <li>Correction of incomplete, inaccurate, or outdated data;</li>
      <li>
        Anonymization, blocking, or deletion of unnecessary, excessive data or data processed in violation of the LGPD;
      </li>
      <li>Data portability to another service or product provider, subject to trade and industrial secrets;</li>
      <li>
        Deletion of personal data processed with the data subject’s consent, except in the cases provided for in the
        LGPD;
      </li>
      <li>Information on public and private entities with which Aviva has carried out shared use of data;</li>
      <li>Information about the possibility of refusing to provide consent and the consequences of refusal;</li>
      <li>Withdrawal of consent.</li>
    </ul>

    <p>
      It is important to note that there are situations governed by other laws that may restrict or limit some of these
      rights, such as situations involving trade and industrial secrets.
    </p>

    <p>
      All requests will be handled upon request and free of charge, subject to prior verification of your identity. Our
      contact details are available in: “<a href="#contact-us">10. CONTACT US</a>”.
    </p>

    <h2>7 SECURITY MEASURES</h2>

    <p>
      Aviva is committed to security and uses various technical and administrative measures to ensure the integrity of
      your personal data.
    </p>

    <p>
      We inform you that our service providers, suppliers, and partners undergo <i>due diligence</i>, approval, and
      legal review processes.
    </p>

    <p>
      For the performance of internal activities, we seek to follow standards and best practices adopted by the market.
    </p>

    <h2>8 USE OF COOKIES</h2>

    <h3>8.1 What is it?</h3>
    <p>
      A cookie is a small computer file that may or may not be sent by a website to the user’s browser when the user
      visits the website. Cookies may be used to improve browsing and remember information about the website visitor’s
      activity, enabling faster and more efficient navigation for the user.
    </p>

    <h3>8.2 How does our website use them?</h3>
    <ul>
      <li>
        <b>Essential cookies:</b> those that are essential for the operation of our websites, as well as for the use of
        certain features;
      </li>
      <li>
        <b>Performance cookies:</b> intended to enhance your browsing experience on our websites, making it smoother and
        more pleasant;
      </li>
      <li>
        <b>Analytics cookies:</b> may collect information about how you browse and interact with our websites, which
        pages were visited, and when they were accessed;
      </li>
      <li>
        <b>Marketing cookies:</b> used to offer discounts and promotions that may be of interest to you. If you choose
        to subscribe to our newsletters, we may send you other exclusive offers;
      </li>
      <li>
        <b>Advertising cookies:</b> used to target advertising and are intended to display ads that may be of interest
        to you;
      </li>
      <li>
        <b>Third-party cookies:</b> used to analyze the use of our website, enable sharing on social networks, and
        provide advertising.
      </li>
    </ul>

    <h3>8.3 How to disable?</h3>
    <p>
      You may, at any time, change your internet browser settings to disable cookies. To learn how to disable them,
      please consult the documentation for your respective browser. Please note that by disabling cookies, your browsing
      experience on our website may become limited or certain features may be affected.
    </p>

    <h2>9 CHANGES TO THIS POLICY</h2>

    <p>
      Aviva reserves the right to change this Privacy and Cookies Policy at any time to reflect any relevant changes in
      legislation or in our practices.
    </p>

    <p>
      We recommend that you revisit this Policy periodically whenever you have questions, and if you have any inquiries,
      please feel free to contact us. Our information is available in: “<a href="#contact-us">10. CONTACT US</a>”.
    </p>

    <h2 id="contact-us">10 CONTACT US</h2>

    <p>
      If you have any questions about this Privacy and Cookies Policy, even after reading it, or if for any reason you
      need to contact us regarding matters involving your personal data, Aviva provides the channels below:
    </p>

    <ul>
      <li>
        <b>Person in Charge of Processing Personal Data:</b> Data Guide Consultoria e Soluções em Proteção de Dados
        LTDA. (CNPJ: 39.317.591/0001-60), through Taynara Rodrigues Bernardo, legal representative of Data Guide;
      </li>
      <li>
        <b>Service Channel:</b>
        <a href="/portaldeprivacidade">https://www.aviva.com.br/portaldeprivacidade</a>;
      </li>
      <li>
        <b>Mailing address:</b> Rua Particular Complexo Turístico, Rio Quente Resorts, S/N, Esplanada, Rio Quente/GO –
        CEP 75.667-000.
      </li>
    </ul>

    <p>We are available to answer your questions and help keep you in control of your personal data.</p>
  </section>

  <section v-else class="container">
    <h1>1 SOBRE NUESTRA POLÍTICA</h1>

    <p>
      Aviva (Companhia Thermas do Rio Quente – CNPJ: 01.540.533/0001-29, Sauipe S/A: CNPJ 00.866.577/0001-80, Valetur –
      CNPJ: 02.082.394/0001-08), comprendiendo la importancia del tema y en consonancia con la Ley General de Protección
      de Datos Personales (Ley n.º 13.709/18), refuerza su compromiso con la seguridad de sus datos, priorizando la
      ética y el cumplimiento de la legislación.
    </p>

    <p>
      Por respeto a su privacidad y valorando la transparencia, Aviva creó esta Política de Privacidad y Cookies para
      informarle qué datos personales se recopilan, utilizan, almacenan y comparten, así como cuáles son sus derechos
      como titular de datos personales.
    </p>

    <p>
      Esta política se aplica a las personas (“Titular de los datos”, “Titular”, “Usted”) que interactúan con los
      productos y servicios de Aviva —ya sean clientes o potenciales clientes, terceros, visitantes de nuestro sitio web
      o candidatos a vacantes de empleo— y a quienes Aviva trate sus datos personales.
    </p>

    <h2>2 FINALIDADES, DATOS RECOPILADOS Y BASES LEGALES DEL TRATAMIENTO</h2>

    <p>
      Los datos personales se recopilan y tratan con base en las siguientes bases legales (hipótesis de tratamiento)
      admitidas por la LGPD:
    </p>

    <table>
      <thead>
        <tr>
          <th>Finalidades</th>
          <th>Datos recopilados</th>
          <th>Hipótesis de tratamiento</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td data-label="Finalidades">Ofertas de productos y servicios</td>
          <td data-label="Datos recopilados">
            Podemos recopilar los siguientes datos:
            <ul class="table-list">
              <li>• Nombre e iniciales – Nombre;</li>
              <li>
                • Características personales – Estado civil | Ocio e intereses | Preferencia de compra | Perfil
                conductual;
              </li>
              <li>
                • Información de contacto – Dirección personal | Teléfono | Correo electrónico personal | Número de
                teléfono móvil personal;
              </li>
              <li>• Información profesional - Ocupación/Cargo.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">Interés legítimo (art. 7, inc. IX, LGPD).</td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Adquisición por parte del cliente de servicios, productos, hospedaje y paquetes de viaje, así como reserva
            de hotel
          </td>
          <td data-label="Datos recopilados">
            Podemos recopilar los siguientes datos:
            <ul class="table-list">
              <li>• Nombre e iniciales - Nombre completo;</li>
              <li>• Características personales - Fecha de nacimiento | Sexo;</li>
              <li>• Identificación emitida por organismos oficiales - CPF | RG | Pasaporte;</li>
              <li>
                • Información de contacto - Dirección residencial | Teléfono residencial | Correo electrónico personal |
                Número de teléfono móvil personal;
              </li>
              <li>• Información financiera – Datos de tarjeta de crédito/débito;</li>
              <li>• Información de niños - Nombre | Fecha de nacimiento | Sexo | CPF | RG | Pasaporte;</li>
              <li>• Información de adolescentes - Nombre | Fecha de nacimiento | Sexo | CPF | RG | Pasaporte.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">
            Los datos se recopilan para el cumplimiento de obligación legal (Decreto n.º 7.381, de 2 de diciembre de
            2010 c/c art. 7, inc. I, LGPD c/c art. 26 de la Ley 11.771/2008 y art. 7, IV, b del Reglamento General de
            los Medios de Hospedaje), así como para solicitud de reembolso, en los casos aplicables, y, internamente,
            para conciliación de pagos, emisión de facturas y otras rutinas operativas internas.<br /><br />Durante la
            vigencia del contrato, los datos se tratarán con base en la hipótesis de ejecución de contrato (art. 7, inc.
            V, LGPD). Finalizada la relación entre las Partes, los datos se mantendrán por el plazo de prescripción, así
            como por el legalmente exigido, de acuerdo con la hipótesis de tratamiento de ejercicio regular de derechos
            (art. 7, inc. VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Adquisición por parte del cliente de entradas de Hot Park y Ocios Opcionales.
          </td>
          <td data-label="Datos recopilados">
            Podemos recopilar los siguientes datos:
            <ul class="table-list">
              <li>• Nombre e iniciales - Nombre completo;</li>
              <li>• Características personales - Fecha de nacimiento | Sexo;</li>
              <li>• Identificación emitida por organismos oficiales - CPF | RG;</li>
              <li>
                • Información de contacto - Dirección residencial | Teléfono residencial | Correo electrónico personal.
              </li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">
            Los datos se utilizan para la ejecución de contrato (art. 7, inc. V, LGPD) que, una vez finalizado, permite
            mantener los datos por el período de prescripción y por el legalmente exigido de acuerdo con la hipótesis de
            tratamiento de ejercicio regular de derechos (art. 7, inc. VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">Navegación en nuestros sitios web</td>
          <td data-label="Datos recopilados">
            <ul class="table-list">
              <li>
                • Datos del dispositivo, información sobre el uso del sitio (hora de acceso, páginas visitadas),
                registros de acceso.
              </li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">
            En el caso de los registros de acceso, se recopilan para el cumplimiento de obligación legal (art. 15 del
            Marco Civil de Internet); en cuanto a los demás, los datos se recopilan de acuerdo con el interés legítimo
            (art. 7, inc. IX, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Atención al cliente<br />(este contacto puede abarcar diversos asuntos, tales como: respuestas a dudas,
            respuestas a reclamaciones y solicitudes, actualizaciones de los pedidos realizados, entre otros, atención
            de solicitudes a través de nuestra Central de Atención)
          </td>
          <td data-label="Datos recopilados">
            <ul class="table-list">
              <li>• Nombre e iniciales - Nombre completo;</li>
              <li>
                • Características personales - Edad | Fecha de nacimiento | Lugar de nacimiento | Género | Nacionalidad
                | Lugar de origen (naturalidad);
              </li>
              <li>• Identificación emitida por organismos oficiales - CPF | RG | Pasaporte;</li>
              <li>
                • Información de contacto - Dirección residencial | Teléfono residencial | Correo electrónico personal |
                Número de teléfono móvil personal;
              </li>
              <li>• Información profesional - Ocupación/Cargo;</li>
              <li>• Información de niños - Nombre | Fecha de nacimiento | Dirección;</li>
              <li>• Información de adolescentes - Nombre | Fecha de nacimiento | Dirección;</li>
              <li>• Información de dispositivos móviles - Correo electrónico - contenido (dispositivos móviles).</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">
            Dependerá de la finalidad de la atención.<br />Si se trata de un posible cliente interesado en los servicios
            ofrecidos, el tratamiento se realizará de acuerdo con la hipótesis de tratamiento de consentimiento (art. 7,
            inc. I, LGPD) o de procedimientos preliminares a la ejecución de contrato (art. 7, inc. V, LGPD); si hay un
            avance, en este caso podrá utilizarse para la ejecución de contrato (art. 7, inc. V, LGPD), hipótesis de
            tratamiento que también se utiliza para las atenciones realizadas en los casos en que la atención se dirige
            a titulares que ya son clientes de Aviva.<br />Los datos, en cualquier caso, se mantendrán por el período de
            prescripción, así como por el legalmente exigible, de acuerdo con la hipótesis de tratamiento de ejercicio
            regular de derechos (art. 7, inc. VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Identificarle en el acceso y durante su permanencia en las instalaciones de Aviva
          </td>
          <td data-label="Datos recopilados">
            <ul class="table-list">
              <li>• Nombre e iniciales - Nombre completo;</li>
              <li>• Identificación emitida por organismos oficiales – CPF;</li>
              <li>• Información de contacto - Número de teléfono móvil personal;</li>
              <li>• Información de niños – Nombre.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">Interés legítimo (art. 7, inc. IX, LGPD).</td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Proporcionar contenido personalizado y recomendaciones con base en sus actividades en nuestros servicios
          </td>
          <td data-label="Datos recopilados">
            <ul class="table-list">
              <li>• Nombre e iniciales - Nombre completo;</li>
              <li>
                • Información de contacto - Ciudad | Teléfono residencial | Correo electrónico personal | Número de
                teléfono móvil personal;
              </li>
              <li>• Información profesional - Ocupación/Cargo.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">Interés legítimo (art. 7, inc. IX, LGPD).</td>
        </tr>
        <tr>
          <td data-label="Finalidades">Suministro de información obligatoria por ley – Completar la FNRH</td>
          <td data-label="Datos recopilados">
            <ul class="table-list">
              <li>• Identificación emitida por organismos oficiales - CPF | RG | Pasaporte;</li>
              <li>
                • Información de contacto - Dirección residencial | Teléfono residencial | Correo electrónico personal |
                Número de teléfono móvil personal;
              </li>
              <li>• Motivo del viaje y duración de la estancia;</li>
              <li>• Otros viajes realizados al destino y en el año;</li>
              <li>• Información profesional - Ocupación/Cargo;</li>
              <li>• Información de niños - Nombre | Fecha de nacimiento | Dirección;</li>
              <li>• Información de adolescentes - Nombre | Fecha de nacimiento | Dirección;</li>
              <li>• Información de dispositivos móviles - Correo electrónico - contenido (dispositivos móviles).</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">
            Los datos se recopilan para el cumplimiento de obligación legal (Decreto n.º 7.381, de 2 de diciembre de
            2010 c/c art. 7, inc. I, LGPD c/c art. 26 de la Ley 11.771/2008 y art. 7, IV, b del Reglamento General de
            los Medios de Hospedaje).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Contratación de colaboradores y/o prestadores de servicios tercerizados, así como la realización del
            respectivo proceso de selección
          </td>
          <td data-label="Datos recopilados">
            <ul class="table-list">
              <li>• Nombre e iniciales - Nombre completo;</li>
              <li>
                • Características personales - Edad | Fecha de nacimiento | Lugar de nacimiento | Género | Nacionalidad
                | Lugar de origen (naturalidad) | Estado civil | Número de hijos | Preferencias de navegación en
                Internet | Perfil conductual;
              </li>
              <li>• Filiación - Nombre de la madre | Nombre del padre;</li>
              <li>• Identificación emitida por organismos oficiales - CPF | RG | CNH | CTPS | Pasaporte;</li>
              <li>
                • Información de contacto - Dirección residencial | Correo electrónico personal | Número de teléfono
                móvil personal;
              </li>
              <li>
                • Escolaridad - Diplomas y escolaridad | Licencias y asociación profesional | Historial académico |
                Certificados;
              </li>
              <li>
                • Información profesional - Ocupación/Cargo | Documentos de organismos de clase | Dirección comercial |
                Teléfono comercial | Correo electrónico comercial | Teléfono móvil comercial | Número de identificación:
                Matrícula;
              </li>
              <li>• Información financiera - Datos bancarios (banco, agencia y cuenta);</li>
              <li>• Información de niños - Nombre | Edad | Sexo;</li>
              <li>• Información de adolescentes - Nombre | Edad | Sexo;</li>
              <li>
                • Datos sensibles generales - Creencias religiosas o filosóficas | Orientación sexual | Raza u Origen
                étnico | Datos de salud ocupacional | Fotografía | Archivo de video.
              </li>
              <li>• Historial profesional</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">
            En el caso de un proceso de selección, los datos se tratarán con base en la hipótesis de procedimientos
            preliminares a la ejecución de contrato (art. 7, inc. V, LGPD); si el proceso avanza, los datos se
            utilizarán para la ejecución de contrato (art. 7, inc. V, LGPD). En este caso, algunos datos también serán
            necesarios por obligación legal o regulatoria (art. 7, inc. II, LGPD c/c art. 11, inc. II, letra "a",
            LGPD).<br /><br />Al finalizar el contrato con el colaborador o prestador de servicios, los datos se
            mantendrán por el plazo legalmente previsto o por el plazo de prescripción, de acuerdo con la hipótesis de
            tratamiento de ejercicio regular de derechos (art. 7, inc. VI, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">Medidas de prevención y combate al fraude y a ilícitos</td>
          <td data-label="Datos recopilados">
            <ul class="table-list">
              <li>• Nombre e iniciales - Nombre completo;</li>
              <li>• Características personales - Fecha de nacimiento | Sexo;</li>
              <li>• Identificación emitida por organismos oficiales – CPF;</li>
              <li>
                • Información de contacto - Dirección residencial | Teléfono residencial | Correo electrónico personal |
                Número de teléfono móvil personal;
              </li>
              <li>• Información de niños - Nombre | Fecha de nacimiento | Sexo;</li>
              <li>• Información de adolescentes - Nombre | Fecha de nacimiento | Sexo.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">
            Protección de crédito (art. 7, inc. X, LGPD) e intereses legítimos (art. 7, inc. IX, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">Eventos, divulgación de acciones promocionales, transmisiones en vivo</td>
          <td data-label="Datos recopilados">
            <ul class="table-list">
              <li>• Información de contacto (nombre, teléfono, redes sociales, correo electrónico).</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">
            La hipótesis de tratamiento dependerá de la etapa del recorrido del cliente.<br />En el caso inicial de la
            recopilación, la hipótesis será el consentimiento (art. 7, inc. I, LGPD).<br /><br />Si hay avance hacia la
            contratación, la hipótesis de tratamiento será la de procedimientos preliminares a la ejecución de contrato
            (art. 7, inc. V, LGPD).<br /><br />Avanzando, los datos se utilizarán para la ejecución de contrato (art. 7,
            inc. V, LGPD).
          </td>
        </tr>
        <tr>
          <td data-label="Finalidades">
            Contenidos generados por usted y con divulgación autorizada: contenidos creados o compartidos públicamente
            por usted, cuando haya autorización previa.
          </td>
          <td data-label="Datos recopilados">
            <ul class="table-list">
              <li>• Recomendaciones, testimonios, evaluaciones, comentarios, imágenes, fotos, entre otros.</li>
            </ul>
          </td>
          <td data-label="Hipóteses de tratamiento">Consentimiento (art. 7, inc. I, LGPD).</td>
        </tr>
      </tbody>
    </table>

    <h2>3 CON QUIÉN PODEMOS COMPARTIR LOS DATOS PERSONALES</h2>

    <p>
      Aviva podrá compartir sus datos si usted lo solicita o, además, cuando sea necesario para una finalidad legítima,
      para la oferta de productos y servicios, compartiéndolos con prestadores de servicios a fin de cumplir el
      propósito comercial de Aviva, todo de acuerdo con la tabla de finalidades y con las hipótesis de tratamiento de
      datos presentadas más arriba. De este modo, el intercambio de sus datos también podrá realizarse con nuestros
      prestadores de servicios y socios, o con autoridades gubernamentales, conforme se indica a continuación:
    </p>

    <ul>
      <li>
        <b>Prestadores de servicios, proveedores y socios:</b> para que podamos brindarle servicios y experiencias de
        calidad, contamos con la colaboración de diversos prestadores de servicios y socios que podrán tratar los datos
        personales recopilados en nuestro nombre y de acuerdo con nuestras instrucciones. Entre los servicios prestados,
        podremos contar con: instituciones financieras, servicios de adquirencia, análisis antifraude, diversos
        prestadores de servicios de tecnología para análisis del comportamiento de consumo, generación de prospectos
        (leads), oferta de productos y servicios y cumplimiento del propósito comercial de Aviva, reclutamiento y
        selección de candidatos, campañas de marketing, eventos y atracciones, y almacenamiento en la nube;
      </li>
      <li>
        <b>Autoridades gubernamentales:</b> para el cumplimiento de obligaciones legales o regulatorias, sus datos
        personales podrán ser compartidos con las autoridades competentes;
      </li>
      <li>En cumplimiento de órdenes judiciales.</li>
    </ul>

    <h2>4 TRANSFERENCIA INTERNACIONAL DE DATOS PERSONALES</h2>

    <p>
      Algunos de sus datos personales podrán transferirse a otros países, cuando sea necesario para la ejecución de
      servicios relacionados con las actividades de Aviva, tales como el uso de servicios informáticos en la nube,
      soluciones tecnológicas o soporte operativo. O incluso con la colaboración de socios que almacenan sus datos
      internacionalmente.
    </p>

    <p>
      Aviva cuenta con acreditación junto a una empresa de intercambio, quedando admitida la extensión del derecho de
      uso de ocupación por Sistema de Tiempo Compartido al sistema de intercambio de hospedaje. Así, en caso de adhesión
      al programa de intercambio de semanas de hospedaje, será necesario completar el contrato de la empresa de
      intercambio y sus datos podrán ser compartidos con ella para realizar la afiliación, mediante un contrato propio
      al que el consumidor se adhiere/contrata de forma adicional. Tras la afiliación, el pago de tasas, agendamientos,
      reservas, entre otros, serán negociados entre el cliente y la empresa de intercambio.
    </p>

    <p>
      Siempre que exista la necesidad de transferencia internacional de datos personales, Aviva adoptará los mecanismos
      y salvaguardas previstos en la Ley General de Protección de Datos Personales (LGPD), especialmente los
      establecidos en el art. 33, así como observará las directrices y reglamentos expedidos por la Agencia Nacional de
      Protección de Datos (ANPD).
    </p>

    <p>
      En los términos de la Resolución CD/ANPD n.º 32, de 26 de enero de 2026, la Unión Europea fue reconocida como
      organismo internacional que proporciona un nivel de protección de datos personales adecuado al previsto en la
      LGPD. Así, las transferencias de datos personales a los Estados-miembros de la Unión Europea, a los países
      integrantes del Espacio Económico Europeo (Islandia, Liechtenstein y Noruega), así como a las instituciones,
      órganos y agencias de la Unión Europea, podrán ocurrir con fundamento en el art. 33, inciso I, de la LGPD, sin
      perjuicio del cumplimiento de las demás obligaciones legales aplicables al tratamiento de datos personales.
    </p>

    <h2>5 RETENCIÓN Y ELIMINACIÓN DE SUS DATOS PERSONALES</h2>

    <p>
      Aviva tratará sus datos personales durante todo el período en que usted sea nuestro cliente, nuestro colaborador,
      utilice nuestros servicios y navegue en nuestras plataformas o sea nuestro potencial cliente con quien compartimos
      nuestras ofertas y promociones. Los datos se almacenarán en un entorno seguro y controlado para garantizar su
      privacidad.
    </p>

    <p>
      Para el cumplimiento de obligaciones legales o regulatorias, para el ejercicio regular de derechos en procesos
      judiciales, administrativos o arbitrales o, cuando corresponda, para fines de auditorías, podremos almacenar sus
      datos personales por un período adicional, de acuerdo con la legislación aplicable.
    </p>

    <p>
      Los plazos de retención de los datos personales observan la finalidad del tratamiento, las obligaciones legales y
      regulatorias aplicables, así como las políticas internas de Aviva relativas a la retención y descarte de datos, en
      los términos del artículo 16 de la LGPD.
    </p>

    <h2>6 SUS DERECHOS COMO TITULAR DE DATOS PERSONALES</h2>

    <p>
      La transparencia sobre el tratamiento de sus datos personales es prioridad para Aviva. Además de la información
      disponible en esta política, usted también podrá ejercer los derechos previstos en el art. 18 de la LGPD, entre
      ellos:
    </p>

    <ul>
      <li>Confirmación de la existencia de tratamiento;</li>
      <li>Acceso a los datos;</li>
      <li>Corrección de datos incompletos, inexactos o desactualizados;</li>
      <li>
        Anonimización, bloqueo o eliminación de datos innecesarios, excesivos o tratados en desacuerdo con la LGPD;
      </li>
      <li>
        Portabilidad de los datos a otro proveedor de servicio o producto, observados los secretos comercial e
        industrial;
      </li>
      <li>
        Eliminación de los datos personales tratados con el consentimiento del titular, excepto en las hipótesis
        previstas en la LGPD;
      </li>
      <li>Información sobre las entidades públicas y privadas con las cuales Aviva realizó uso compartido de datos;</li>
      <li>Información sobre la posibilidad de no otorgar consentimiento y sobre las consecuencias de la negativa;</li>
      <li>Revocación del consentimiento.</li>
    </ul>

    <p>
      Es importante señalar que existen situaciones reguladas por otras leyes que podrán restringir o limitar algunos de
      estos derechos, tales como situaciones que involucren secretos comerciales e industriales.
    </p>

    <p>
      Todas las solicitudes serán tratadas mediante requerimiento y de forma gratuita, y estarán sujetas a una
      evaluación previa de su identidad. Nuestros contactos están disponibles en el ítem: “<a href="#contactenos"
        >10. CONTÁCTENOS</a
      >”.
    </p>

    <h2>7 MEDIDAS DE SEGURIDAD</h2>

    <p>
      Aviva tiene un compromiso con la seguridad y utiliza diversos tipos de medidas técnicas y administrativas para
      garantizar la integridad de sus datos personales.
    </p>

    <p>
      Informamos que nuestros prestadores de servicios, proveedores y socios pasan por un proceso de
      <i>due diligence</i>, homologación y análisis jurídico.
    </p>

    <p>
      Para la ejecución de las actividades internas, buscamos orientarnos por estándares y mejores prácticas adoptadas
      por el mercado.
    </p>

    <h2>8 USO DE COOKIES</h2>

    <h3>8.1 ¿Qué es?</h3>
    <p>
      Una cookie es un pequeño archivo informático que puede o no ser enviado por un sitio de internet al navegador del
      usuario, cuando el usuario visita el sitio. Las cookies pueden utilizarse para mejorar la navegación y recordar
      información sobre la actividad del visitante del sitio, permitiendo una navegación más rápida y eficiente para el
      usuario.
    </p>

    <h3>8.2 ¿Cómo las utiliza nuestro sitio web?</h3>
    <ul>
      <li>
        <b>Cookies esenciales:</b> son aquellas esenciales para el funcionamiento de nuestros sitios web, así como para
        el uso de determinados recursos;
      </li>
      <li>
        <b>Cookies de rendimiento:</b> buscan mejorar su experiencia de navegación en nuestros sitios web, haciéndola
        más fluida y agradable;
      </li>
      <li>
        <b>Cookies analíticas:</b> pueden recopilar información sobre cómo navega e interactúa con nuestros sitios web,
        qué páginas se visitaron y cuándo;
      </li>
      <li>
        <b>Cookies de marketing:</b> se utilizan para ofrecer descuentos y promociones que puedan ser de su interés. Si
        desea registrarse en nuestros boletines, podremos enviarle otras ofertas exclusivas;
      </li>
      <li>
        <b>Cookies de publicidad:</b> sirven para orientar la publicidad y buscan mostrar anuncios que puedan ser de su
        interés;
      </li>
      <li>
        <b>Cookies de terceros:</b> sirven para analizar el uso de nuestro sitio web, posibilitar el intercambio en
        redes sociales y proporcionar publicidad.
      </li>
    </ul>

    <h3>8.3 ¿Cómo desactivar?</h3>
    <p>
      Usted puede, en cualquier momento, modificar su navegador de internet para desactivar las cookies. Para verificar
      el procedimiento de desactivación, consulte la documentación de su respectivo navegador. Recordamos que, al
      desactivarlas, su navegación en nuestro sitio web podrá volverse limitada o con algunas funcionalidades
      comprometidas.
    </p>

    <h2>9 CAMBIOS EN ESTA POLÍTICA</h2>

    <p>
      Aviva se reserva el derecho de modificar esta Política de Privacidad y Cookies en cualquier momento, para reflejar
      cualesquiera cambios relevantes en la legislación o en nuestras prácticas.
    </p>

    <p>
      Sugerimos que usted revise periódicamente esta política siempre que tenga dudas y, si tiene alguna pregunta, no
      dude en contactarnos. Nuestra información está disponible en el ítem: “<a href="#contactenos">10. CONTÁCTENOS</a
      >”.
    </p>

    <h2 id="contactenos">10 CONTÁCTENOS</h2>

    <p>
      Siempre que usted tenga alguna duda sobre esta Política de Privacidad y Cookies, incluso después de su lectura, o
      por cualquier razón necesite comunicarse con nosotros sobre asuntos relacionados con sus datos personales, Aviva
      pone a su disposición los canales a continuación:
    </p>

    <ul>
      <li>
        <b>Encargado del Tratamiento de Datos Personales:</b> Data Guide Consultoria e Soluções em Proteção de Dados
        LTDA. (CNPJ: 39.317.591/0001-60), por intermedio de Taynara Rodrigues Bernardo, representante legal de Data
        Guide;
      </li>
      <li>
        <b>Canal de Atención:</b>
        <a href="/portaldeprivacidade">https://www.aviva.com.br/portaldeprivacidade</a>;
      </li>
      <li>
        <b>Correspondencia:</b> Rua Particular Complexo Turístico, Rio Quente Resorts, S/N, Esplanada, Rio Quente/GO –
        CEP 75.667-000.
      </li>
    </ul>

    <p>Quedamos a su disposición para aclarar sus dudas y ponerle en el control de sus datos personales.</p>
  </section>
</template>

<style scoped>
.container {
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #000;
  max-width: 900px;
  padding: 10px 20px;
  margin: 0 auto;
  background-color: #fff;
  overflow-wrap: break-word;
}

.container:first-of-type {
  margin-top: 130px;
}

.container:last-of-type {
  margin-bottom: 50px;
}

h1,
h2 {
  color: #005a5a;
  font-weight: normal;
}
h1 {
  font-size: 24px;
  margin-top: 40px;
  margin-bottom: 20px;
}
h2 {
  font-size: 20px;
  margin-top: 40px;
  margin-bottom: 20px;
}
h3 {
  color: #005a5a;
  font-size: 18px;
  font-weight: normal;
  margin-top: 30px;
}
p {
  text-align: justify;
  margin-bottom: 15px;
}
ul {
  margin-top: 5px;
  margin-bottom: 15px;
  padding-left: 20px;
}
ul li {
  margin-bottom: 8px;
  text-align: justify;
}

/* Estilos da Tabela (Desktop) */
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  margin-bottom: 30px;
  font-size: 14px;
}
th,
td {
  border: 1px solid #000;
  padding: 10px;
  vertical-align: top;
  text-align: left;
}
th {
  background-color: #bfbfbf;
  font-weight: bold;
  text-align: center;
}
.table-list {
  list-style-type: none;
  padding-left: 0;
  margin: 0;
}
.table-list li {
  margin-bottom: 10px;
  text-align: left;
}
a {
  color: #0000ee;
  text-decoration: underline;
}

@media screen and (max-width: 768px) {
  .container:first-of-type {
    margin-top: 80px;
  }
  table,
  thead,
  tbody,
  th,
  td,
  tr {
    display: block;
  }
  thead tr {
    display: none;
  }
  tr {
    margin-bottom: 25px;
    border: 2px solid #005a5a;
    border-radius: 8px;
    padding: 10px;
    background-color: #fcfcfc;
  }
  td {
    border: none;
    border-bottom: 1px solid #ddd;
    padding: 15px 0;
  }
  td:last-child {
    border-bottom: none;
  }
  td::before {
    content: attr(data-label);
    display: block;
    font-weight: bold;
    color: #005a5a;
    margin-bottom: 10px;
    font-size: 16px;
    text-transform: uppercase;
  }
}
</style>
